import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useEntityOptions } from '../hooks/useEntityOptions';
import { EntitySelect } from '../components/shared/EntitySelect';
import { toast } from 'sonner';
import {
  Bell, Plus, Search, Clock, CheckCircle2, AlertTriangle,
  Trash2, RefreshCw, Pause, Play, Calendar
} from 'lucide-react';
import { cn } from '../lib/utils';
import { ExportButton } from '../components/shared/ExportButton';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  snoozed: 'bg-blue-100 text-blue-700',
  done: 'bg-emerald-100 text-emerald-700',
};

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card className="bg-white border-gray-border" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-medium">{label}</p>
          <p className="text-xl sm:text-2xl font-bold text-text-primary mt-1">{value}</p>
        </div>
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", color)}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function FollowUpRemindersPage() {
  const [followups, setFollowups] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ contact_name: '', due_date: '', note: '', priority: 'medium', reminder_type: 'manual' });
  const { contacts } = useEntityOptions();

  const fetchData = useCallback(async () => {
    try {
      const [fups, dash] = await Promise.all([
        axios.get(`${API}/api/followups`),
        axios.get(`${API}/api/followups/dashboard`),
      ]);
      setFollowups(fups.data.followups || []);
      setDashboard(dash.data);
    } catch { toast.error('Failed to load follow-ups'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.contact_name.trim()) { toast.error('Contact name required'); return; }
    try {
      await axios.post(`${API}/api/followups`, form);
      toast.success('Follow-up created!');
      setShowCreate(false);
      setForm({ contact_name: '', due_date: '', note: '', priority: 'medium', reminder_type: 'manual' });
      fetchData();
    } catch { toast.error('Failed to create'); }
  };

  const markComplete = async (id) => {
    try {
      await axios.put(`${API}/api/followups/${id}/complete`, {});
      toast.success('Completed!');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const snooze = async (id, hours = 24) => {
    try {
      await axios.put(`${API}/api/followups/${id}/snooze`, { hours });
      toast.success(`Snoozed for ${hours}h`);
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const deleteFollowup = async (id) => {
    if (!window.confirm('Delete this follow-up?')) return;
    try {
      await axios.delete(`${API}/api/followups/${id}`);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const filtered = followups.filter(f => {
    if (filterStatus !== 'all' && f.status !== filterStatus) return false;
    if (search && !f.contact_name?.toLowerCase().includes(search.toLowerCase()) && !f.note?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const isOverdue = (f) => f.status === 'pending' && f.due_date && f.due_date < new Date().toISOString().slice(0, 10);

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-6 w-6 animate-spin text-gray-medium" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="followup-reminders-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Follow-Up Reminders<PageInfoTooltip pageKey="follow-ups" /></h1>
          <p className="text-gray-medium text-sm mt-1">Track and manage all your follow-up activities</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            data={followups.map(f => ({ contact: f.contact_name, due: f.due_date, priority: f.priority, status: f.status, note: f.note }))}
            filename="follow_ups"
            columns={['contact', 'due', 'priority', 'status', 'note']}
          />
          <Button onClick={() => setShowCreate(true)} data-testid="create-followup-btn">
            <Plus className="h-4 w-4 mr-1" /> New Follow-Up
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Bell} label="Total" value={dashboard.total || 0} color="bg-blue-50 text-blue-600" />
        <StatCard icon={Clock} label="Pending" value={dashboard.pending || 0} color="bg-amber-50 text-amber-600" />
        <StatCard icon={AlertTriangle} label="Overdue" value={dashboard.overdue || 0} color="bg-red-50 text-red-600" />
        <StatCard icon={CheckCircle2} label="Completed" value={dashboard.completed || 0} color="bg-emerald-50 text-emerald-600" />
      </div>

      <Card className="bg-white border-gray-border">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input placeholder="Search follow-ups..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} data-testid="followup-search" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="snoozed">Snoozed</SelectItem>
                <SelectItem value="done">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-medium">
              <Bell className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>No follow-ups yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(f => {
                const overdue = isOverdue(f);
                return (
                  <div key={f.id} className={cn("flex items-center gap-3 p-3 rounded-lg border transition-colors", overdue ? "border-red-200 bg-red-50/30" : "border-gray-border hover:bg-gray-50")} data-testid={`followup-row-${f.id}`}>
                    <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0", overdue ? "bg-red-100" : "bg-amber-50")}>
                      {f.status === 'done' ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : overdue ? <AlertTriangle className="h-4 w-4 text-red-600" /> : <Bell className="h-4 w-4 text-amber-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-text-primary">{f.contact_name || 'Unnamed'}</span>
                        <Badge className={cn("text-[10px]", STATUS_COLORS[f.status])}>{f.status}</Badge>
                        <Badge className={cn("text-[10px]", PRIORITY_COLORS[f.priority])}>{f.priority}</Badge>
                        {overdue && <Badge className="text-[10px] bg-red-100 text-red-700">Overdue</Badge>}
                      </div>
                      <p className="text-xs text-gray-medium truncate mt-0.5">{f.note || 'No notes'}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs font-medium text-text-primary">{f.due_date || '—'}</p>
                      <p className="text-[10px] text-gray-medium capitalize">{f.reminder_type}</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {f.status === 'pending' && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => markComplete(f.id)} title="Complete"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /></Button>}
                      {f.status === 'pending' && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => snooze(f.id)} title="Snooze 24h"><Pause className="h-3.5 w-3.5 text-blue-500" /></Button>}
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteFollowup(f.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New Follow-Up</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Contact Name *</Label><EntitySelect value={form.contact_name} onChange={v => setForm(f => ({ ...f, contact_name: v }))} options={contacts} placeholder="Select or type contact name" listId="followup-contacts" inputProps={{ "data-testid": "followup-contact-name" }} /></div>
            <div><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} /></div>
            <div><Label>Priority</Label>
              <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['low', 'medium', 'high', 'urgent'].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Type</Label>
              <Select value={form.reminder_type} onValueChange={v => setForm(f => ({ ...f, reminder_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['manual', 'proposal_not_viewed', 'deal_idle', 'lead_no_activity'].map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Note</Label><Textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={handleCreate} data-testid="save-followup-btn">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
